import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Avatar from '../../base/avatar/components/Avatar';
import { getParticipantById, getParticipantDisplayName, isScreenShareParticipantById } from '../../base/participants/functions';
import { getVideoTrackByParticipant } from '../../base/tracks/functions.any';
import SecondScreenVideo from './SecondScreenVideo';
/**
 * The styles, injected into the second window via its own Emotion cache (see
 * {@code SecondScreenPortals}). The dominant-speaker ring colour matches the main
 * window's thumbnail indicator (VietMeet's action01 token).
 */
const useStyles = makeStyles()(() => {
    return {
        tile: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            backgroundColor: '#292929',
            border: '3px solid transparent',
            borderRadius: 4,
            overflow: 'hidden',
            transition: 'border-color 0.2s ease'
        },
        speaking: {
            borderColor: '#4687ED'
        },
        avatar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        bottom: {
            position: 'absolute',
            bottom: 6,
            left: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            maxWidth: 'calc(100% - 12px)',
            padding: '3px 6px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: 4,
            color: '#fff'
        },
        name: {
            minWidth: 0,
            fontSize: 12,
            lineHeight: '16px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }
    };
});
/**
 * Renders one participant's tile: their camera or shared screen (via the
 * realm-safe {@link SecondScreenVideo} clone leaf) or their avatar, with their
 * name and a dominant-speaker ring. Used both as a gallery cell and inside a
 * stage filmstrip button. Memoized so a dominant-speaker change only re-renders
 * the tiles whose ring flips.
 *
 * @param {IProps} props - The component props.
 * @returns {ReactElement}
 */
const SecondScreenTile = ({ height, isActiveSpeaker, participantId, width, win }) => {
    const { classes, cx } = useStyles();
    const name = useSelector((state) => getParticipantDisplayName(state, participantId));
    const isScreenshare = useSelector((state) => isScreenShareParticipantById(state, participantId));
    const videoTrack = useSelector((state) => getVideoTrackByParticipant(state, getParticipantById(state, participantId)));
    const track = videoTrack?.vietmeetTrack && !videoTrack.muted
        ? videoTrack.vietmeetTrack.getTrack() ?? null
        : null;
    const avatarSize = Math.max(24, Math.floor(Math.min(height / 2, width - 30, 150)));
    return (React.createElement("div", { className: cx(classes.tile, isActiveSpeaker && classes.speaking), style: { height: `${height}px`, width: `${width}px` } },
        track ? (React.createElement(SecondScreenVideo, { fit: isScreenshare ? 'contain' : 'cover', track: track, win: win })) : (React.createElement("div", { className: classes.avatar },
            React.createElement(Avatar, { participantId: participantId, size: avatarSize }))),
        React.createElement("div", { className: classes.bottom },
            React.createElement("span", { className: classes.name }, name))));
};
export default React.memo(SecondScreenTile);
